import type { FC, ReactNode } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import { useClickOutside } from "@hooks/useClickOutside";
import { useComboboxBlur } from "@hooks/useComboboxBlur";
import { SingleSelectListOption } from "../ComboboxListOption";
import { ComboboxGroupButton } from "../ComboboxButton";
import { ComboboxListbox } from "../ComboboxListbox";
import { ComboboxSearchInput } from "../ComboboxSearchInput";
import type { SelectedItem } from "@services/filter-api/types";
import { trackButtonClick, trackUserInput } from "@services/segment";

export interface ComboboxButtonWrapperProps {
  breakpoint?: string;
  button?: "select" | "submit";
  buttonWrapperClass?: string;
  centeredFilters?: boolean;
  data: {
    value: string;
    label: string;
    searched?: boolean;
    selected?: boolean;
    filtered?: boolean;
  }[];
  endIcon?: ReactNode;
  error?: string;
  handleGetSelectedItem?: (item: SelectedItem) => void;
  handleGetSelectedItems?: (
    items: {
      label: string;
      value: string;
      order: number;
      selected: boolean;
    }[],
  ) => void;
  handleOpenClick?: (open: boolean) => void;
  hideSearch?: boolean;
  id?: string;
  isMobile?: boolean;
  label?: string;
  name?: string;
  new_styles?: boolean;
  open?: boolean;
  optional?: boolean;
  placeholder?: string;
  rebrand?: boolean;
  required?: boolean;
  selectedItem?: {
    value: string;
    label: string;
  } | null;
  startIcon?: ReactNode;
  tabbedFilters?: boolean;
  variant?: "contained" | "outlined";
}
export const ComboboxButtonWrapper: FC<ComboboxButtonWrapperProps> = ({
  breakpoint,
  button = "select",
  buttonWrapperClass,
  centeredFilters,
  data,
  endIcon,
  error,
  handleGetSelectedItem,
  handleOpenClick,
  hideSearch,
  id,
  isMobile,
  label,
  name,
  new_styles,
  open,
  optional,
  placeholder,
  rebrand,
  selectedItem,
  startIcon,
  tabbedFilters,
  variant = "outlined",
}) => {
  const comboBoxRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<
    {
      abbreviation?: string;
      filtered?: boolean;
      label: string;
      order: number;
      searched?: boolean;
      selected: boolean;
      slug: string;
      value: string;
    }[]
  >(
    data.map((_item: any) => {
      return {
        ..._item,
        selected: false,
      };
    }),
  );

  useEffect(() => {
    if (JSON.stringify(items) !== JSON.stringify(data)) {
      setItems(
        items
          .sort((a, b) => a.order - b.order)
          .map((_item) => {
            const isFiltered = data.find(
              (_v) => _item.value === _v.value && _v.filtered,
            );
            if (isFiltered) {
              return {
                ..._item,
                filtered: true,
                searched: false,
              };
            }
            return {
              ..._item,
              filtered: false,
            };
          }
        ),
      );
    }
  }, [data]);

  useEffect(() => {
    if (selectedItem) {
      setItems(
        items
          .sort((a, b) => a.order - b.order)
          .map((_item) => {
            return {
              ..._item,
              selected: _item.value === selectedItem.value,
            };
          }
        ),
      );
    } else {
      // Handle deselection - set all items to not selected
      setItems(
        items
          .sort((a, b) => a.order - b.order)
          .map((_item) => {
            return {
              ..._item,
              selected: false,
            };
          }
        ),
      );
    }
  }, [selectedItem?.label, selectedItem?.value]);

  const handleClick = () => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
    }

    handleOpenClick?.(!open);

    trackButtonClick(name || "");
  };
  const handleClose = () => {
    // Blur the search input to trigger the onBlur event
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
    
    document.body.style.overflow = '';
    handleOpenClick?.(false);
  };

  useClickOutside(comboBoxRef, () => {
    if (!isMobile) {
      handleClose();
    }
  });

  const handleBlur = useComboboxBlur(comboBoxRef, handleClose, isMobile);

  const handleSelectOption = (
    option: { value: string; label: string },
    checked: boolean,
  ) => {
    setSearchTerm("");
    const updatedItems = items.map((_item) => {
      return {
        ..._item,
        selected: _item.value === option.value ? checked : false
      }
    });
    setItems(updatedItems);
    const selected = updatedItems.find((_item) => _item.value === option.value);
    if (selected && selected.selected) {
      handleGetSelectedItem?.(selected);
    } else if (selected) {
      handleGetSelectedItem?.({
        value: selected.value,
        label: selected.label,
        selected: false,
        slug: selected.slug || '',
        abbreviation: selected.abbreviation
      });
    }
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    if (term) {
      const filteredItems = items.map((_item, index) => {
        // This was added for the location list search
        // This tries to match the state abbreviation first and prioritizes that
        if (_item?.abbreviation?.toLowerCase() === term.toLowerCase()) {
          return {
            ..._item,
            searched: true,
            weight: 1,
          };
        }

        if (_item.label.toLowerCase().includes(term.toLowerCase())) {
          return {
            ..._item,
            searched: true,
            weight: index,
          };
        }

        return {
          ..._item,
          searched: false,
          weight: 0,
        };
      })
        .sort((a, b) => a.weight < b.weight ? -1 : a.weight > b.weight ? 1 : 0)
        .sort((a, b) => a.order - b.order);

      setItems(filteredItems);
    } else {
      setItems(items.map((_item) => ({ ..._item, searched: false })));
    }
  };

  const handleSearchBlur = (term: string) => {
    // Only track if there's actually a search term and name is provided
    if (term.trim() && name) {
      trackUserInput(`${name} Search`, term.trim());
    }
  };

  const renderedItems = () => {
    const fItems = items.filter((_item) => _item.filtered);
    if (searchTerm) {
      let sItems: {
        label: string;
        value: string;
        selected: boolean;
        searched?: boolean;
        filtered?: boolean;
      }[] = [];

      if (fItems.length) {
        sItems = items.filter((_item) => {
          return _item.searched && _item.filtered;
        });
      }
      if (!fItems.length) {
        sItems = items.filter((_item) => {
          return _item.searched;
        });
      }
      return sItems.map((_v) => (
        <SingleSelectListOption
          handleClose={handleClose}
          key={_v.value}
          value={_v.value}
          label={_v.label}
          rebrand={rebrand}
          selected={_v.selected}
          handleSelectOption={handleSelectOption}
        />
      ));
    }
    if (fItems.length) {
      return fItems.map((_v) => (
        <SingleSelectListOption
          handleClose={handleClose}
          key={_v.value}
          value={_v.value}
          label={_v.label}
          rebrand={rebrand}
          selected={_v.selected}
          handleSelectOption={handleSelectOption}
        />
      ));
    }

    if (!searchTerm && !fItems.length) {
      return items.map((_v) => (
        <SingleSelectListOption
          handleClose={handleClose}
          key={_v.value}
          value={_v.value}
          label={_v.label}
          rebrand={rebrand}
          selected={_v.selected}
          handleSelectOption={handleSelectOption}
        />
      ));
    }
  };

  return (
    <ComboboxGroupButton
      breakpoint={breakpoint}
      button={button}
      buttonWrapperClass={buttonWrapperClass}
      centeredFilters={centeredFilters}
      endIcon={endIcon}
      error={error}
      id={id}
      isMobile={isMobile}
      label={label}
      labels={items
        .filter((_item) => _item.selected)
        .map((_item) => _item.label)}
      name={name}
      new_styles={new_styles}
      onBlur={handleBlur}
      onClick={handleClick}
      open={open}
      placeholder={placeholder}
      rebrand={rebrand}
      ref={comboBoxRef}
      startIcon={startIcon}
      tabbedFilters={tabbedFilters}
      variant={variant}
    >
      {({ open }) => {
        {
          return open ? (
            <ComboboxListbox
              handleClose={handleClose}
              placeholder={placeholder}
              optional={optional}
              isMobile={isMobile}
              rebrand={rebrand}
              items={items.filter((_item) => _item.filtered)}
            >
              {!hideSearch && (
                <ComboboxSearchInput
                  ref={searchInputRef}
                  term={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleSearchBlur={handleSearchBlur}
                  rebrand={rebrand}
                />
              )}
              <>{renderedItems()}</>
            </ComboboxListbox>
          ) : null;
        }
      }}
    </ComboboxGroupButton>
  );
};
